import { ReactNode } from 'react';
import { SxProps, Tab, Tabs, TabsProps, Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';

export type AppTabItem<T extends string = string> = {
  value: T;
  label: ReactNode;
  disabled?: boolean;
};

export type AppTabsAppearance =
  | 'segmented'
  | 'minimal';

export type AppTabsProps<T extends string = string> = Omit<
  TabsProps,
  'children' | 'value' | 'onChange'
> & {
  value: T;
  tabs: readonly AppTabItem<T>[];
  onChange: (value: T) => void;
  tabSx?: SxProps<Theme>;
  appearance?: AppTabsAppearance;
};

export const AppTabs = <T extends string>({
  value,
  tabs,
  onChange,
  sx,
  tabSx,
  appearance = 'segmented',
  ...rest
}: AppTabsProps<T>) => {
  const segmentedRootSx: SxProps<Theme> = {
    display: 'inline-flex',
    alignSelf: 'flex-start',
    minHeight: 40,
    p: 0.45,
    borderRadius: 2,
    border: '1px solid',
    borderColor: (theme) => theme.designSystem.surface.panelBorder,
    background: (theme) => theme.designSystem.surface.gridHeaderBackground,
    '& .MuiTabs-indicator': {
      display: 'none'
    },
    '& .MuiTab-root': {
      minHeight: 32,
      px: 1.5,
      textTransform: 'none',
      fontWeight: 600,
      borderRadius: 1.25,
      border: '1px solid transparent',
      color: 'text.secondary',
      transition: 'all 120ms ease',
      position: 'relative',
      zIndex: 1
    },
    '& .MuiTab-root:hover': {
      background: (theme) => theme.designSystem.state.rowHover,
      color: 'text.primary'
    },
    '& .MuiTab-root:focus-visible': {
      outline: 'none',
      boxShadow: (theme) =>
        `0 0 0 2px ${theme.designSystem.state.rowSelectedBorder}`
    },
    '& .MuiTab-root.Mui-selected': {
      color: 'text.primary',
      borderColor: (theme) => theme.designSystem.state.rowSelectedBorder,
      background: (theme) => theme.designSystem.state.rowSelectedGradient
    },
    '& .MuiTab-root.Mui-selected:hover': {
      background: (theme) => theme.designSystem.state.rowSelectedHoverGradient
    },
    '& .MuiTab-root.Mui-disabled': {
      opacity: 0.45
    }
  };

  const minimalRootSx: SxProps<Theme> = {
    display: 'flex',
    alignSelf: 'stretch',
    width: '100%',
    minHeight: 50,
    p: 0,
    borderBottom: '1px solid',
    borderColor: (theme) => alpha(theme.palette.common.white, 0.08),
    background: 'transparent',
    '& .MuiTabs-indicator': {
      display: 'none'
    },
    '& .MuiTabs-flexContainer': {
      gap: 2
    },
    '& .MuiTab-root': {
      flex: 1,
      minHeight: 50,
      px: 0.5,
      py: 0.5,
      textTransform: 'none',
      fontWeight: 600,
      borderRadius: 0,
      color: 'text.secondary',
      transition: 'all 140ms ease',
      alignItems: 'stretch',
      position: 'relative',
      overflow: 'visible'
    },
    '& .MuiTab-root::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: -1,
      height: 2,
      borderRadius: 999,
      background: 'transparent',
      transition: 'all 140ms ease'
    },
    '& .MuiTab-root:hover': {
      color: 'text.primary'
    },
    '& .MuiTab-root:hover::after': {
      background: (theme) => alpha(theme.palette.primary.light, 0.32)
    },
    '& .MuiTab-root:focus-visible': {
      outline: 'none',
      boxShadow: (theme) =>
        `0 0 0 2px ${theme.designSystem.state.rowSelectedBorder}`
    },
    '& .MuiTab-root.Mui-selected': {
      color: 'text.primary'
    },
    '& .MuiTab-root.Mui-selected::after': {
      background: (theme) =>
        `linear-gradient(90deg, ${alpha(theme.palette.primary.light, 0.96)}, ${alpha(theme.palette.primary.main, 0.72)})`
    },
    '& .MuiTab-root.Mui-disabled': {
      opacity: 0.45
    },
    '& .MuiTab-root .app-tabs-label': {
      width: '100%',
      display: 'grid',
      gap: 0.18,
      justifyItems: 'start',
      textAlign: 'left'
    },
    '& .MuiTab-root [data-tab-title]': {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.7,
      lineHeight: 1.1
    },
    '& .MuiTab-root [data-tab-caption]': {
      fontSize: 10.5,
      lineHeight: 1.1,
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
      opacity: 0.62
    },
    '& .MuiTab-root .app-tabs-icon-shell': {
      width: 20,
      height: 20,
      borderRadius: 999,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'transparent',
      border: 'none'
    },
    '& .MuiTab-root .MuiSvgIcon-root': {
      fontSize: 16,
      opacity: 0.82
    }
  };

  const rootSx = appearance === 'minimal' ? minimalRootSx : segmentedRootSx;
  const mergedSx: SxProps<Theme> = Array.isArray(sx)
    ? [rootSx, ...sx]
    : sx
      ? [rootSx, sx]
      : rootSx;

  return (
    <Tabs
      value={value}
      onChange={(_, nextValue) => onChange(nextValue as T)}
      sx={mergedSx}
      {...rest}
    >
      {tabs.map((tab) => (
        <Tab
          key={tab.value}
          value={tab.value}
          label={tab.label}
          disabled={tab.disabled}
          sx={tabSx}
        />
      ))}
    </Tabs>
  );
};
